import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { toErrMsg, registerStepUpHandler } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ShieldCheck, KeyRound, RefreshCw } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { translations as _tr, t as _tt } from "@/lib/i18n";   // STEP 7b · D1: the dialog in the screen language (text only)

/**
 * Global step-up 2FA dialog.
 *
 * Registered via `registerStepUpHandler(...)` on mount. When any API call
 * returns 428 STEP_UP_REQUIRED, the dialog opens, emails the current user a
 * 6-digit code, and — on success — resolves so the api interceptor can retry
 * the original request.
 */
export default function StepUpDialog() {
  const { t: _t } = useI18n() || {};
  const tr = (k) => (_t ? _t(`s7b.stepUp.${k}`) : _tt(_tr.en, `s7b.stepUp.${k}`));   // no provider (tests): English
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [devCode, setDevCode] = useState("");
  const [deliveryFailed, setDeliveryFailed] = useState(false);
  const inputRef = useRef(null);
  const resolveRef = useRef(null);
  const rejectRef = useRef(null);

  const issue = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/stepup/challenge", null, { _silent: true });
      setChallengeId(r.data.challenge_id);
      setEmailHint(r.data.email_hint || "");
      if (r.data.dev_code) setDevCode(r.data.dev_code); else setDevCode("");
      setDeliveryFailed(!!r.data.delivery_failed);
    } catch (e) {
      toast.error(toErrMsg(e, tr("sendFailed")));
      handleClose(false);
    } finally { setBusy(false); }
  };

  const handleClose = (verified) => {
    setOpen(false);
    setCode(""); setChallengeId(""); setDevCode(""); setEmailHint("");
    if (verified) resolveRef.current && resolveRef.current();
    else rejectRef.current && rejectRef.current(new Error("Step-up cancelled"));
    resolveRef.current = null; rejectRef.current = null;
  };

  useEffect(() => {
    registerStepUpHandler(() => new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
      setOpen(true);
      // fire off the email code
      setTimeout(() => issue(), 50);
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 2FA step-up: registered once on purpose; a dep change re-registers the handler (owner rule, no 2FA change)
  }, []);

  useEffect(() => {
    if (open && !busy && challengeId) inputRef.current?.focus();
  }, [open, busy, challengeId]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code || !challengeId) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/stepup/verify",
                       { challenge_id: challengeId, code }, { _silent: true });
      toast.success(tr("verified"));
      handleClose(true);
    } catch (err) {
      toast.error(toErrMsg(err, tr("verifyFailed")));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose(false)}>
      <DialogContent data-testid="stepup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            {tr("title")}
          </DialogTitle>
          <DialogDescription>
            {emailHint
              ? (() => { const [a, b] = tr("descEmail").split("{email}"); return <>{a}<b><bdi dir="ltr">{emailHint}</bdi></b>{b ?? ""}</>; })()
              : tr("desc")}
          </DialogDescription>
        </DialogHeader>
        {devCode && (
          <div className="rounded border border-amber-400 bg-amber-50 p-2 text-xs text-amber-900">
            <b>Dev fallback code:</b> <code>{devCode}</code>
          </div>
        )}
        {deliveryFailed && !devCode && (
          <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
            {tr("deliveryFailed")}
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <Input
            ref={inputRef}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="123456"
            inputMode="numeric"
            maxLength={6}
            autoComplete="one-time-code"
            className="text-center text-lg tracking-widest"
            data-testid="stepup-code-input"
          />
          <Button type="button" size="sm" variant="ghost" onClick={issue}
                    disabled={busy}
                    className="text-xs text-emerald-600 hover:bg-emerald-50"
                    data-testid="stepup-resend">
            <RefreshCw className="me-1 h-3 w-3" /> {tr("resend")}
          </Button>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}
                    data-testid="stepup-cancel">{tr("cancel")}</Button>
          <Button onClick={submit} disabled={busy || code.length !== 6}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="stepup-verify">
            <KeyRound className="me-1 h-3 w-3" />
            {busy ? tr("verifying") : tr("verify")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
